import { useEffect, useRef, type FormEvent } from "react";
import { platformBrand } from "../../shared/branding";
import type { PinProtectedAction } from "./pin-gate-state";

type Props = {
  open: boolean;
  action: PinProtectedAction | null;
  pin: string;
  error: string | null;
  busy: boolean;
  privateLocked: boolean;
  entryRequired?: boolean;
  onPinChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDiscardPrivateForm: () => void;
};

const actionText: Record<PinProtectedAction["kind"], string> = {
  OPEN: "Formu açmak için PIN’inizi girin. Yarım kalan formunuz varsa devam edeceksiniz.",
  SAVE: "Kaydetmek için operatör PIN’inizi girin.",
  SUBMIT: "Formu göndermek için operatör PIN’inizi girin.",
};

export function OperatorPinDialog({
  open,
  action,
  pin,
  error,
  busy,
  privateLocked,
  entryRequired = false,
  onPinChange,
  onSubmit,
  onCancel,
  onDiscardPrivateForm,
}: Props) {
  const entryScreen = entryRequired && !privateLocked;
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, [open]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (pin.length === 4 && !busy) onSubmit();
  };

  return (
    <dialog
      ref={dialogRef}
      className="shift-pin-dialog"
      data-entry-screen={entryScreen || undefined}
      aria-labelledby="shift-pin-title"
      aria-describedby="shift-pin-description"
      onCancel={(event) => {
        event.preventDefault();
        if (!privateLocked && !entryRequired && !busy) onCancel();
      }}
    >
      {entryScreen ? <header className="shift-pin-brand"><img src="/anl-logo-new-2026.webp" alt="ANL" /><span><strong>{platformBrand.name}</strong><small>{platformBrand.description}</small></span></header> : null}
      <form onSubmit={submit} aria-busy={busy}>
        <h2 id="shift-pin-title">
          {privateLocked ? "Kayıt kilitli" : entryScreen ? "Vardiya üretim formu" : "Operatör doğrulaması"}
        </h2>
        <p id="shift-pin-description">
          {privateLocked
            ? "Bu kayıt sahibinin PIN’i doğrulanana kadar form içeriği gizli tutulur."
            : action
              ? actionText[action.kind]
              : "Devam etmek için operatör PIN’inizi girin."}
        </p>
        <label htmlFor="shift-operator-pin">4 haneli operatör PIN’i</label>
        <input
          id="shift-operator-pin"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          pattern="[0-9]{4}"
          maxLength={4}
          autoFocus
          value={pin}
          aria-describedby={error ? "shift-pin-error" : undefined}
          onChange={(event) => onPinChange(event.target.value)}
        />
        {error ? (
          <p
            id="shift-pin-error"
            className="shift-pin-dialog-error"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <div className="shift-pin-dialog-actions">
          {privateLocked ? (
            <button
              type="button"
              disabled={busy}
              onClick={onDiscardPrivateForm}
            >
              Formu kapat ve yeni form aç
            </button>
          ) : !entryRequired ? (
            <button type="button" disabled={busy} onClick={onCancel}>
              Vazgeç
            </button>
          ) : null}
          <button
            type="submit"
            className="is-primary"
            disabled={busy || pin.length !== 4}
          >
            {busy ? "Doğrulanıyor…" : "PIN’i doğrula"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
